import useKitApi, { KitPackage, KitPackageForm, KitPackageShape } from "../../api/KitApi";
import { Identifiable, NamedIdentifiable } from "../../model/Items";

interface Quantifiable {
  quantity: number;
}

interface NamedQuantifiable
extends Quantifiable {
  id: number;
  name?: string; 
}

interface KitForm
extends Quantifiable {
  form: NamedIdentifiable,
}

interface KitShape
extends Quantifiable {
  shape: NamedIdentifiable,
}

const convertKitPackageFormToQuantifiable = (kitPackageForm: KitPackageForm) : NamedQuantifiable =>
  convertKitPackageElementToQuantifiable(kitPackageForm.form, kitPackageForm.quantity);

const convertKitPackageShapeToQuantifiable = (kitPackageShape: KitPackageShape) : NamedQuantifiable =>
  convertKitPackageElementToQuantifiable(kitPackageShape.shape, kitPackageShape.quantity);

const convertKitPackageElementToQuantifiable = (kitPackageElement: Identifiable, quantity: number) : NamedQuantifiable =>
  ({ id: kitPackageElement.id, quantity });

const convertKitFormToQuantifiable = (kitForm: KitForm) : NamedQuantifiable =>
  ({ id: kitForm.form.id, quantity: kitForm.quantity, name: kitForm.form.name });

const convertKitShapeToQuantifiable = (kitShape: KitShape) : NamedQuantifiable =>
  ({ id: kitShape.shape.id, quantity: kitShape.quantity, name: kitShape.shape.name });

const preparePackedKitElements = (kitPackageElements: NamedQuantifiable[]) : NamedQuantifiable[] => {
  const packedKitElements: NamedQuantifiable[] = [];
  kitPackageElements.forEach(packageElement => {
    const packedKitElement = packedKitElements.find(kitElement => kitElement.id === packageElement.id);
    if (packedKitElement === undefined) {
      packedKitElements.push(packageElement);
    } else {
      packedKitElement.quantity += packageElement.quantity;
    }
  });
  return packedKitElements;
};

const registerInconsistencies = (
  inconsistencies: string[],
  elementType: string,
  specifiedKitElements: NamedQuantifiable[],
  packedKitElements: NamedQuantifiable[]
) => {
  specifiedKitElements.forEach(specifiedKitElement => {
    const packedKitElement = packedKitElements.find(packedKitElement => packedKitElement.id === specifiedKitElement.id);
    if (packedKitElement === undefined) {
      inconsistencies.push(`Brak specyfikacji pakowania dla ${elementType} ${specifiedKitElement.name}.`);
    } else if (packedKitElement.quantity != specifiedKitElement.quantity) {
      inconsistencies.push(`
        Niezgodna liczba elementów dla ${elementType} ${specifiedKitElement.name}
        (w komplecie: ${specifiedKitElement.quantity} - w paczkach: ${packedKitElement.quantity}).`);
    }
  });
};

const registerKitFormsInconsistencies = (
  kitPackages: KitPackage[],
  kitForms: any[],
  inconsistencies: string[]
) => {
  const packedKitForms: NamedQuantifiable[] = preparePackedKitElements(
    kitPackages.flatMap(kitPackage => kitPackage.packageForms).map(convertKitPackageFormToQuantifiable));
  registerInconsistencies(inconsistencies, 'formatki', kitForms.map(convertKitFormToQuantifiable), packedKitForms);
};

const registerKitShapesInconsistencies = (
  kitPackages: KitPackage[],
  kitShapes: any[],
  inconsistencies: string[]
) => {
  const packedKitShapes: NamedQuantifiable[] = preparePackedKitElements(
    kitPackages.flatMap(kitPackage => kitPackage.packageShapes).map(convertKitPackageShapeToQuantifiable));
  registerInconsistencies(inconsistencies, 'wykroju', kitShapes.map(convertKitShapeToQuantifiable), packedKitShapes);
};

const useKitInconsistencyDetector = () => {
  const kitApi = useKitApi();

  const detectInconsistenciesBetweenKitSpecificationAndPackagingSpecification = async (
    customerId: number,
    kitId: number,
  ) : Promise<string[]> => {
    const inconsistencies : string[] = [];
    const kitPackages = await kitApi.fetchKitPackages(customerId, kitId);
    const kitForms: any[] = await kitApi.fetchKitForms(customerId, 0, kitId); // TODO: hardcoded 0 as priceListId
    const kitShapes: any[] = await kitApi.fetchKitShapes(customerId, 0, kitId); // TODO: hardcoded 0 as priceListId
    registerKitFormsInconsistencies(kitPackages, kitForms, inconsistencies);
    registerKitShapesInconsistencies(kitPackages, kitShapes, inconsistencies);
    return inconsistencies;
  };

  const reportInconsistenciesBetweenKitSpecificationAndPackagingSpecification = async (
    customerId: number,
    kitId: number,
    reportAlerts: (messages: string[]) => void
  ) => {
    const inconsistencies = await detectInconsistenciesBetweenKitSpecificationAndPackagingSpecification(customerId, kitId);
    if (inconsistencies.length > 0) {
      reportAlerts(inconsistencies);
    }
  };

  return {
    detectInconsistenciesBetweenKitSpecificationAndPackagingSpecification,
    reportInconsistenciesBetweenKitSpecificationAndPackagingSpecification
  };
}

export default useKitInconsistencyDetector;